// @ts-nocheck
import { LucideIcon } from "lucide-react";
import { cn } from "@libraryadmin/lib/utils";

interface StatCardProps {
  title: string;
  value: string | number;
  icon: LucideIcon;
  trend?: { value: number; label: string };
  variant?: "default" | "primary" | "secondary" | "warning" | "destructive";
  className?: string;
  delay?: number;
}

const variantStyles = {
  default: "bg-card border-border/50",
  primary:
    "bg-gradient-to-br from-primary/[0.02] to-primary/[0.08] border-primary/20 hover:border-primary/40 hover:shadow-[0_8px_30px_rgba(5,77,59,0.08)]",
  secondary:
    "bg-gradient-to-br from-secondary/[0.02] to-secondary/[0.08] border-secondary/20 hover:border-secondary/40 hover:shadow-[0_8px_30px_rgba(18,74,86,0.08)]",
  warning:
    "bg-gradient-to-br from-warning/[0.02] to-warning/[0.08] border-warning/20 hover:border-warning/40 hover:shadow-[0_8px_30px_rgba(230,142,0,0.08)]",
  destructive:
    "bg-gradient-to-br from-destructive/[0.02] to-destructive/[0.08] border-destructive/20 hover:border-destructive/40 hover:shadow-[0_8px_30px_rgba(239,68,68,0.08)]",
};

const iconStyles = {
  default: "bg-muted text-muted-foreground",
  primary:
    "bg-primary/10 text-primary group-hover:scale-110 transition-transform duration-300",
  secondary:
    "bg-secondary/10 text-secondary group-hover:scale-110 transition-transform duration-300",
  warning:
    "bg-warning/10 text-warning group-hover:scale-110 transition-transform duration-300",
  destructive:
    "bg-destructive/10 text-destructive group-hover:scale-110 transition-transform duration-300",
};

export function StatCard({
  title,
  value,
  icon: Icon,
  trend,
  variant = "default",
  className,
  delay = 0,
}: StatCardProps) {
  return (
    <div
      className={cn(
        "stat-card animate-fade-in group cursor-pointer border opacity-0",
        variantStyles[variant],
        className
      )}
      style={{
        animationDelay: `${delay * 0.1}s`,
        animationFillMode: "forwards",
      }}
    >
      <div className="flex items-start justify-between">
        <div>
          <p className="text-sm font-medium text-muted-foreground transition-colors duration-300 group-hover:text-foreground">
            {title}
          </p>
          <p className="mt-2 text-3xl font-extrabold tracking-tight text-foreground">
            {value}
          </p>
          {trend && (
            <p
              className={cn(
                "mt-2.5 flex items-center gap-1 text-xs font-semibold",
                trend.value >= 0 ? "text-success" : "text-destructive"
              )}
            >
              <span className="text-sm leading-none">
                {trend.value >= 0 ? "↑" : "↓"}
              </span>
              <span>{Math.abs(trend.value)}%</span>
              <span className="font-normal text-muted-foreground">
                {trend.label}
              </span>
            </p>
          )}
        </div>
        <div
          className={cn(
            "rounded-xl p-3 transition-all duration-300",
            iconStyles[variant]
          )}
        >
          <Icon className="h-6 w-6" />
        </div>
      </div>
    </div>
  );
}
